/**
 * Inside a tile: what the page around it may ask before closing it (I-3641).
 * `/tile-closing` is an empty route: going there runs the route-leave guards of
 * whatever the tile shows, in the tile, with the app's own confirmation. The page
 * also opens records here and tells this tile which others it can open into.
 */
import { isNavigationFailure, type Router } from "vue-router";
import {
  connectTileHasUnsavedWork,
  exposeConnectTileFrame,
  reportConnectTile,
  type ConnectTileCommand,
} from "@blpde/connect";
import { rowOpenTiles } from "./tileTargets";

export const TILE_CLOSING_PATH = "/tile-closing";

const reportInput = () => reportConnectTile({ pressed: true });

function hearInput(target: EventTarget): void {
  target.addEventListener("pointerdown", reportInput, true);
  target.addEventListener("keydown", reportInput, true);
}

export function installTileFrame(router: Router): void {
  exposeConnectTileFrame({
    hasUnsavedWork: connectTileHasUnsavedWork,
    async leave() {
      return !isNavigationFailure(await router.push(TILE_CLOSING_PATH));
    },
  });
  window.addEventListener(
    "message",
    (event: MessageEvent<ConnectTileCommand>) => {
      if (event.source !== window.parent || event.origin !== location.origin)
        return;
      if (event.data?.type === "connect.tile.navigate")
        void router.push(event.data.path);
      if (event.data?.type === "connect.tile.targets")
        rowOpenTiles.value = event.data.tiles;
    },
  );
  // The active tile is the one the user works in, and input in a frame reaches the
  // page around it only this way. Not on focus: a tile loading behind another one
  // may take that.
  hearInput(window);
  // A document nested here (a mail's body, an editor) keeps its input to itself.
  document.addEventListener(
    "load",
    (event) => {
      if (
        event.target instanceof HTMLIFrameElement &&
        event.target.contentDocument
      )
        hearInput(event.target.contentDocument);
    },
    true,
  );
  reportConnectTile({ booted: true });
}
